import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { SmokeBackground } from '../components/ui/spooky-smoke-animation';
import {
  FiCheck,
  FiArrowRight,
  FiCode,
  FiBarChart2,
  FiShield,
  FiZap,
  FiTrendingUp,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import useScrollToSection from '../hooks/useScrollToSection';

// ========= EXTRACTED COMPONENTS (Rules of Hooks compliant) =========

const PAPIA_SLIDES = [
  {
    src: '/zap.png',
    alt: 'Automação da PapIA integrada ao WhatsApp',
    aspectRatio: '717 / 1562',
    cardClassName: 'w-[72%] max-w-[315px]'
  },
  {
    src: '/papia2.png',
    alt: 'Visão geral do sistema PapIA',
    aspectRatio: '1023 / 827',
    cardClassName: 'w-full lg:w-[115%]',
    expanded: true
  }
];

const DELIVEX_SLIDES = [
  {
    src: '/hashidel.png',
    alt: 'Cardápio digital criado com o DeliveX',
    aspectRatio: '814 / 771',
    cardClassName: 'w-full'
  },
  {
    src: '/delivex2.png',
    alt: 'Gestão de pedidos no DeliveX',
    aspectRatio: '544 / 859',
    cardClassName: 'w-[82%] max-w-[430px]'
  }
];

const AdaptiveImageSlider = ({ slides, label }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [pendingSlide, setPendingSlide] = useState(null);
  const [isImageVisible, setIsImageVisible] = useState(true);
  const revealTimerRef = useRef(null);

  useEffect(() => {
    if (!isImageVisible || pendingSlide !== null) return undefined;

    const timer = window.setTimeout(() => {
      setPendingSlide((activeSlide + 1) % slides.length);
      setIsImageVisible(false);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [activeSlide, isImageVisible, pendingSlide, slides.length]);

  useEffect(() => () => {
    if (revealTimerRef.current) window.clearTimeout(revealTimerRef.current);
  }, []);

  const changeSlide = (nextSlide) => {
    if (!isImageVisible || pendingSlide !== null || nextSlide === activeSlide) return;
    setPendingSlide(nextSlide);
    setIsImageVisible(false);
  };

  const showPrevious = () => {
    changeSlide((activeSlide - 1 + slides.length) % slides.length);
  };

  const showNext = () => {
    changeSlide((activeSlide + 1) % slides.length);
  };

  const handleExitComplete = () => {
    if (pendingSlide === null) return;

    setActiveSlide(pendingSlide);
    setPendingSlide(null);
    revealTimerRef.current = window.setTimeout(() => {
      setIsImageVisible(true);
    }, 320);
  };

  const currentSlide = slides[activeSlide];

  return (
    <div className={`flex w-full ${currentSlide.expanded ? 'justify-end' : 'justify-center'}`}>
      <motion.div
        layout
        className={`flex-shrink-0 rounded-2xl bg-gray-100 p-4 shadow-paper md:p-6 ${currentSlide.cardClassName}`}
        transition={{ layout: { type: 'spring', stiffness: 150, damping: 24, mass: 0.9 } }}
      >
      <motion.div
        layout
        className="relative w-full overflow-hidden rounded-xl bg-[#111214]"
        style={{ aspectRatio: currentSlide.aspectRatio }}
        transition={{ layout: { type: 'spring', stiffness: 150, damping: 24, mass: 0.9 } }}
      >
        <AnimatePresence initial={false} mode="wait" onExitComplete={handleExitComplete}>
          {isImageVisible && (
            <motion.img
              key={currentSlide.src}
              src={currentSlide.src}
              alt={currentSlide.alt}
              className="absolute inset-0 h-full w-full object-contain"
              initial={{ opacity: 0, scale: 0.985, filter: 'blur(12px)' }}
              animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, scale: 1.015, filter: 'blur(12px)' }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              loading="lazy"
              decoding="async"
            />
          )}
        </AnimatePresence>

        <button
          type="button"
          onClick={showPrevious}
          aria-label={`Mostrar imagem anterior da ${label}`}
          className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/75"
        >
          <FiChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={showNext}
          aria-label={`Mostrar próxima imagem da ${label}`}
          className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/75"
        >
          <FiChevronRight className="h-5 w-5" />
        </button>

        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2 rounded-full bg-black/55 px-3 py-2 backdrop-blur-sm">
          {slides.map((slide, index) => (
            <button
              key={slide.src}
              type="button"
              onClick={() => changeSlide(index)}
              aria-label={`Mostrar imagem ${index + 1} da ${label}`}
              aria-current={activeSlide === index ? 'true' : undefined}
              className={`h-2 rounded-full transition-all ${activeSlide === index ? 'w-6 bg-white' : 'w-2 bg-white/45 hover:bg-white/70'}`}
            />
          ))}
        </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

const PapiaImageSlider = () => <AdaptiveImageSlider slides={PAPIA_SLIDES} label="PapIA" />;
const DelivexImageSlider = () => <AdaptiveImageSlider slides={DELIVEX_SLIDES} label="DeliveX" />;

const ServiceDetailItem = ({ service, index, scrollYProgress }) => {
  const textY = useTransform(
    scrollYProgress,
    [index * 0.15, index * 0.15 + 0.3],
    [30, -10]
  );
  const imageY = useTransform(
    scrollYProgress,
    [index * 0.15, index * 0.15 + 0.3],
    [50, -20]
  );
  const smoothTextY = useSpring(textY, { stiffness: 80, damping: 25 });
  const smoothImageY = useSpring(imageY, { stiffness: 60, damping: 25 });

  const isImageIcon = service.gradient === 'from-green-500 to-blue-600' || service.gradient === 'rotas-gradient' || service.gradient === 'lucrum-gradient' || service.gradient === 'delivex-gradient';

  return (
    <motion.div
      id={service.slug}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.2 }}
      className={`mb-24 ${service.slug === 'automacao-whatsapp' ? 'lg:min-h-[620px]' : ''} ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex lg:items-center lg:gap-12`}
    >
      <motion.div
        className="lg:w-1/2 mb-8 lg:mb-0 flex flex-col"
        style={{ y: smoothTextY }}
      >
        <div className="mb-6 flex items-start gap-4 sm:gap-5">
          <div className={`flex w-fit flex-shrink-0 rounded-2xl ${isImageIcon ? '' : 'p-4 bg-gray-100'} text-gray-900`}>
            {service.icon}
          </div>

          <div className="min-w-0 pt-1">
            {['automacao-whatsapp', 'delivex', 'rotas-inteligentes', 'lucrum-financeiro'].includes(service.slug) ? (
              <a
                href={service.slug === 'automacao-whatsapp' ? 'https://papia.aytech.online/' : service.slug === 'delivex' ? 'https://delivery.aytech.online/' : service.slug === 'rotas-inteligentes' ? 'https://rotas.aytech.online/' : 'https://financeiro.aytech.online/'}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-2xl md:text-4xl font-bold text-gray-900 mb-2 hover:text-gray-600 transition-colors duration-300 cursor-pointer tracking-tight"
                title="Clique para abrir a página"
              >
                {service.title}
              </a>
            ) : (
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2 tracking-tight">
                {service.title}
              </h2>
            )}

            <p className="text-sm text-gray-500 font-medium uppercase tracking-widest">
              {service.subtitle}
            </p>

            {['automacao-whatsapp', 'delivex', 'rotas-inteligentes', 'lucrum-financeiro'].includes(service.slug) && (
              <p className="mt-2 text-xs text-gray-400 uppercase tracking-widest">
                Clique no nome para acessar a página
              </p>
            )}
          </div>
        </div>

        <p className="text-gray-500 mb-8 leading-relaxed">
          {service.description}
        </p>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
          {service.features.map((feature, idx) => (
            <div key={idx} className="flex items-center">
              <FiCheck className="w-4 h-4 text-gray-900 mr-3 flex-shrink-0" />
              <span className="text-gray-600 text-sm">{feature}</span>
            </div>
          ))}
        </div>

        {/* Benefits */}
        <div className="bg-gray-50 p-6 rounded-2xl mb-8 border border-gray-100">
          <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-widest">Resultados Comprovados</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {service.benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center">
                <FiTrendingUp className="w-4 h-4 text-gray-600 mr-2 flex-shrink-0" />
                <span className="text-sm text-gray-600">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="text-xl font-bold text-gray-900 tracking-tight">
            {service.price}
          </div>
          <motion.div
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <Link
              to={`/contact?service=${encodeURIComponent(service.title)}`}
              className="inline-flex items-center gap-2 bg-gray-950 hover:bg-gray-700 text-white px-6 py-3 rounded-full font-medium transition-colors duration-300 btn-hover text-sm uppercase tracking-widest"
              style={{ backgroundColor: '#0a0a0a' }}
            >
              Entrar em Contato
              <FiArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        className="lg:w-1/2"
        style={{ y: smoothImageY }}
      >
        {service.slug === 'rotas-inteligentes' ? (
          <div className="bg-gray-100 rounded-2xl p-4 md:p-6 min-h-[560px] md:min-h-[620px] flex items-center justify-center">
            <img
              src="/rotasinfo.png"
              alt="Sistema de Rotas Inteligentes"
              className="w-full h-auto rounded-2xl shadow-paper object-contain"
              loading="lazy"
              decoding="async"
            />
          </div>
        ) : service.slug === 'automacao-whatsapp' ? (
          <PapiaImageSlider />
        ) : service.slug === 'delivex' ? (
          <DelivexImageSlider />
        ) : (
          <div className="bg-gray-100 rounded-2xl p-4 md:p-6 min-h-[560px] md:min-h-[620px] flex items-center justify-center">
            <div className="w-full text-center">
              {service.slug === 'lucrum-financeiro' ? (
                <div className="relative w-full max-w-[720px] mx-auto">
                  <img
                    src="/financeiroinfo.png"
                    alt="Sistema Financeiro Lucrum"
                    className="w-full h-auto rounded-2xl shadow-paper object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ) : (
                <>
                  <div className="inline-flex p-8 rounded-full bg-gray-200 text-gray-700 mb-4">
                    {service.icon}
                  </div>
                  <p className="text-gray-500 text-sm">Em manutenção</p>
                </>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const AdditionalServiceCard = ({ service, index, scrollYProgress }) => {
  const cardY = useTransform(
    scrollYProgress,
    [0.1, 0.5],
    [30 + index * 10, 0]
  );
  const smoothCardY = useSpring(cardY, { stiffness: 80, damping: 25 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      style={{ y: smoothCardY }}
      className="bg-white p-8 rounded-2xl shadow-paper border border-gray-100 text-center card-hover"
    >
      <div className="inline-flex p-4 bg-gray-100 rounded-xl text-gray-900 mb-6">
        {service.icon}
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-4 tracking-tight">{service.title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed">{service.description}</p>
    </motion.div>
  );
};

// ========= MAIN COMPONENT =========

const Services = () => {
  useScrollToSection();

  // ========= PARALLAX REFS & SCROLL =========
  const heroRef = useRef(null);
  const mainRef = useRef(null);
  const additionalRef = useRef(null);
  const ctaRef = useRef(null);

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroTextY = useTransform(heroProgress, [0, 1], [0, 100]);
  const heroTextOpacity = useTransform(heroProgress, [0, 0.6], [1, 0]);
  const heroScale = useTransform(heroProgress, [0, 0.5], [1, 0.97]);

  const { scrollYProgress: mainProgress } = useScroll({
    target: mainRef,
    offset: ["start end", "end start"],
  });

  const { scrollYProgress: additionalProgress } = useScroll({
    target: additionalRef,
    offset: ["start end", "end start"],
  });

  const { scrollYProgress: ctaProgress } = useScroll({
    target: ctaRef,
    offset: ["start end", "end start"],
  });
  const ctaBgY = useTransform(ctaProgress, [0, 1], [30, -30]);
  const smoothCtaBgY = useSpring(ctaBgY, { stiffness: 80, damping: 25 });

  const mainServices = [
    {
      id: 1,
      slug: "automacao-whatsapp",
      icon: <img src="/logopapia.png" alt="PapIA" className="w-20 h-20 object-contain rounded-2xl" />,
      title: "PapIA - Automação de IA",
      subtitle: "Inteligência Artificial com CRM Integrado (CRM fase de testes)",
      description: "Sistema completo de automação para WhatsApp e Instagram com CRM integrado (CRM fase de testes), dashboard avançado com gerenciamento da Inteligência artificial conversional e relatórios de performance.",
      features: [
        "Automação inteligente de respostas",
        "CRM integrado com histórico completo",
        "Dashboard com analytics em tempo real",
        "Integração com sistemas existentes",
        "Chatbot com IA conversacional",
        "Agendamentos automáticos (em fase de testes)",
      ],
      benefits: [
        "Aumento de 200% na conversão de leads",
        "Redução de 95% no tempo de resposta",
        "Automatização de 92% das tarefas repetitivas",
        "Melhoria de 250% na satisfação do cliente"
      ],
      price: "A partir de R$ 89,90/mês",
      gradient: "from-green-500 to-blue-600",
      image: "/api/placeholder/600/400"
    },
    {
      id: 2,
      slug: "delivex",
      icon: <img src="/logodelivex.png" alt="DeliveX" className="w-20 h-20 object-contain rounded-2xl" />,
      title: "DeliveX",
      subtitle: "Plataforma Completa para Gestão de Restaurantes e Delivery",
      description: "Plataforma desenvolvida para centralizar toda a operação de restaurantes, lanchonetes e pizzarias. O DeliveX integra atendimento, vendas, produção, entregas e relacionamento com clientes em um único sistema.",
      features: [
        "Cardápio 100% digital e pedidos online",
        "Nenhuma cobrança por venda, apenas mensalidade fixa",
        "Fidelidade, cupons e relacionamento com clientes",
        "Integração com Lucrum - Sistema Financeiro",
        "Integração com Rotas - Gestão de Entregas",
        "Integração com PapIA - WhatsApp com IA"
      ],
      benefits: [
        "Lucro de até 30% com pedidos diretos, sem taxas de aplicativos",
        "Redução no tempo de atendimento e entrega",
        "Aumento da satisfação do cliente",
        "Maior retorno de clientes e fidelização"
      ],
      price: "A partir de R$ 149,90/mês",
      gradient: "delivex-gradient",
      image: "/hashidel.png"
    },
    {
      id: 3,
      slug: "rotas-inteligentes",
      icon: <img src="/rotasicon.png" alt="Rotas" className="w-20 h-20 object-contain" />,
      title: "Rotas - Gestão de Entregas",
      subtitle: "Sistema Completo de Gestão de Entregas",
      description: "Sistema de gestão de entregas com otimização de rotas por IA, rastreamento em tempo real, integração iFood e analytics avançados. Gerencie entregas com mapas em tempo real e automação de WhatsApp.",
      features: [
        "Mapa de pedidos em tempo real",
        "Otimização de rotas por IA (em desenvolvimento)",
        "Integração com iFood (em desenvolvimento)",
        "Dashboard de analytics avançado",
        "Gestão de motoboys",
        "Integração com WhatsApp (em desenvolvimento)",
        "Histórico completo de entregas",
        "Relatórios de performance"
      ],
      benefits: [
        "Economia de até 30% em combustível",
        "Rastreamento em tempo real",
        "Integração completa iFood",
        "Analytics avançados de entregas"
      ],
      price: "A partir de R$ 129,90/mês",
      gradient: "rotas-gradient",
      image: "/api/placeholder/600/400"
    },
    {
      id: 4,
      slug: "lucrum-financeiro",
      icon: <img src="/lucrumicon.png" alt="Lucrum" className="w-20 h-20 object-contain" />,
      title: "Lucrum - Sistema Financeiro",
      subtitle: "Gestão Financeira para Restaurantes",
      description: "Sistema de gestão financeira inteligente para restaurantes. Controle de caixa, despesas, folha de pagamento e lucros em um único lugar. Simples, rápido e eficiente.",
      features: [
        "Controle de caixa diário",
        "Gestão de despesas",
        "DRE automático",
        "Folha de pagamento",
        "Gestão de motoboys",
        "Relatórios financeiros",
        "Controle de delivery",
        "Dashboard integrado (em desenvolvimento)"
      ],
      benefits: [
        "Visão completa das finanças",
        "Controle de lucros em tempo real",
        "Gestão simplificada de despesas",
        "Relatórios automáticos"
      ],
      price: "A partir de R$ 149,90/mês",
      gradient: "lucrum-gradient",
      image: "/api/placeholder/600/400"
    }
  ];

  const orderedMainServices = [
    'delivex',
    'automacao-whatsapp',
    'lucrum-financeiro',
    'rotas-inteligentes'
  ].map((slug) => mainServices.find((service) => service.slug === slug));

  const additionalServices = [
    {
      icon: <FiCode className="w-6 h-6" />,
      title: "Desenvolvimento personalizado",
      description: "Qualquer dúvida ou necessidade específica que você possa ter, podemos desenvolver uma solução sob medida para você"
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: "Tem uma ideia?",
      description: "Se você tem uma ideia, podemos ajudá-lo(a) a transformá-la em realidade"
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: "Segurança digital",
      description: "Proteção avançada para seus dados e sistemas, com backups diários e monitoramento constante"
    },
    {
      icon: <FiZap className="w-6 h-6" />,
      title: "Negócio novo?",
      description: "Está começando um novo negócio? Podemos ajudá-lo(a) reduzindo o custo da mensalidade inicial"
    }
  ];

  return (
    <div className="relative min-h-screen bg-white">
      {/* Shared fluid background for every dark section */}
      <div className="pointer-events-none fixed inset-0 z-0 bg-[#0a0a0a]" aria-hidden="true">
        <SmokeBackground smokeColor="#2f2f2f" />
      </div>

      {/* Hero Section */}
      <section ref={heroRef} className="pt-44 pb-44 parallax-section">
        <div className="pointer-events-none absolute inset-0 z-0 parallax-grid opacity-20" aria-hidden="true" />

        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
          style={{
            y: heroTextY,
            opacity: heroTextOpacity,
            scale: heroScale,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <span className="text-xs text-white/70 uppercase tracking-widest font-mono">Portfólio</span>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mt-4 mb-6 tracking-tighter leading-tight">
              Nossas Soluções
            </h1>
            <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
              Transforme seu negócio com nossas soluções tecnológicas inovadoras,
              desenvolvidas para maximizar eficiência e resultados.
            </p>
          </motion.div>
        </motion.div>

        {/* Diagonal transition into the services content */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-16 leading-[0] sm:h-20 lg:h-24"
          aria-hidden="true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1000 100"
            preserveAspectRatio="none"
            className="block h-full w-full scale-y-[-1]"
          >
            <g fill="#ffffff">
              <path d="M0 0v100c166.7 0 166.7-66 333.3-66S500 77 666.7 77 833.3 28 1000 28V0H0Z" opacity=".5" />
              <path d="M0 0v100c166.7 0 166.7-66 333.3-66S500 70 666.7 70 833.3 16 1000 16V0H0Z" opacity=".5" />
              <path d="M0 0v100c166.7 0 166.7-66 333.3-66S500 63 666.7 63 833.3 4 1000 4V0H0Z" />
            </g>
          </svg>
        </div>
      </section>

      {/* Main Services */}
      <section ref={mainRef} className="-mt-px pt-12 pb-24 bg-white parallax-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {orderedMainServices.map((service, index) => (
            <ServiceDetailItem
              key={service.id}
              service={service}
              index={index}
              scrollYProgress={mainProgress}
            />
          ))}
        </div>
      </section>

      {/* Additional Services */}
      <section ref={additionalRef} className="-mt-px pb-24 parallax-section">
        {/* Inverted transition from the services content */}
        <div className="pointer-events-none relative z-[1] h-16 leading-[0] sm:h-20 lg:h-24" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1000 100"
            preserveAspectRatio="none"
            className="block h-full w-full scale-x-[-1]"
          >
            <g fill="#ffffff">
              <path d="M0 0v100c166.7 0 166.7-66 333.3-66S500 77 666.7 77 833.3 28 1000 28V0H0Z" opacity=".5" />
              <path d="M0 0v100c166.7 0 166.7-66 333.3-66S500 70 666.7 70 833.3 16 1000 16V0H0Z" opacity=".5" />
              <path d="M0 0v100c166.7 0 166.7-66 333.3-66S500 63 666.7 63 833.3 4 1000 4V0H0Z" />
            </g>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 pt-16 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-14"
          >
            <span className="text-xs text-white/70 uppercase tracking-widest font-mono">Mais serviços</span>
            <h2 className="text-4xl font-display font-bold text-white mt-3 tracking-tighter">
              Serviços adicionais que fornecemos
            </h2>
            <p className="text-white/70 mt-4 max-w-xl text-base leading-relaxed">
              Complementamos nossas soluções principais com estes serviços especializados
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices.map((service, index) => (
              <AdditionalServiceCard
                key={index}
                service={service}
                index={index}
                scrollYProgress={additionalProgress}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-16 parallax-section">
        <motion.div
          className="pointer-events-none absolute inset-0 z-0 parallax-grid opacity-15"
          style={{ y: smoothCtaBgY }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-white/[0.08] px-6 py-16 text-center shadow-[0_24px_80px_rgba(0,0,0,0.35)] backdrop-blur-2xl sm:px-10 sm:py-20"
          >
            <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent" aria-hidden="true" />
            <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-white/[0.07] blur-3xl" aria-hidden="true" />

            <div className="relative z-10">
              <h2 className="text-4xl font-display font-bold text-white mt-4 mb-6 tracking-tighter">
                Pronto para começar?
              </h2>
              <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
                Entre em contato conosco e iremos atende-lo (a) com muito prazer.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-950 font-semibold text-sm uppercase tracking-widest rounded-full transition-all duration-300 hover:bg-gray-100 btn-hover"
                    style={{ color: '#0a0a0a' }}
                  >
                    Entre em contato
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
